// @ts-nocheck

import * as Sentry from '@sentry/react';

import apiActionCreators from '../../../actions/api';
import { externalLinkOpen } from '../../../actions/externallink';
import { sendReport } from '../../../actions/report';
import Client from '../../../client';
import config from '../../../config/app.config';
import { RedirectServices, getRedirectUrl } from '../../../config/urls';
import { getLang } from '../../../i18n/l10n';
import { __ } from '../../../i18n/l10n';
import { getCustomHeaders } from '../../../utils';
import { getGrid } from '../selectors/grids';
import Basket from '../states/basket';
import Selection from '../states/selection';
import { GridsErrorCodes } from '../types';
import { GridsActionTypes } from '../types/actions';
import type {
  OnCheckBoxType,
  InitBasketType,
  InitPublicBasketType,
  InitHistoryBasketType,
  FillPrechecksType,
  SetBasketValidatedType,
  SetBasketErrorType,
  SetBasketPendingType,
  ClearChecksType,
  SwitchTypeType,
  SetStakeType,
  RemoveSystemGridType,
  TogglePrivateGridType,
  ToggleSystemGridType,
  CopySystemGridType,
  CopyGridType,
  SubmitBasketType,
  SetBasketClearType,
  FillRandomlyType,
} from '../types/actions';

const { basketGridUrl } = config;
const { makeApiCall } = apiActionCreators;

export const initBasket: InitBasketType = ({ poolId, selections }) => ({
  type: GridsActionTypes.INIT_BASKET,
  payload: {
    poolId,
    selections,
  },
});

export const initPublicBasket: InitPublicBasketType = ({ poolId, gridReference }) => ({
  type: GridsActionTypes.INIT_PUBLIC_BASKET,
  payload: {
    poolId,
    gridReference,
  },
});

export const initHistoryBasket: InitHistoryBasketType = ({ poolId, gridReference }) => ({
  type: GridsActionTypes.INIT_HISTORY_BASKET,
  payload: {
    poolId,
    gridReference,
  },
});

export const fillPrechecks: FillPrechecksType = ({ poolId, selections }) => ({
  type: GridsActionTypes.FILL_PRECHECKS,
  payload: {
    poolId,
    selections,
  },
});

export const setBasketValidated: SetBasketValidatedType = ({ gridReference }) => ({
  type: GridsActionTypes.SET_BASKET_VALIDATED,
  payload: {
    gridReference,
  },
});

export const clearBasket: SetBasketClearType = () => ({
  type: GridsActionTypes.CLEAR_BASKET,
});

export const fillRandomly: FillRandomlyType = (selectionIndex) => ({
  type: GridsActionTypes.FILL_RANDOMLY,
  payload: {
    selectionIndex,
  },
});

export const setBasketError: SetBasketErrorType = (estr) => ({
  type: GridsActionTypes.SET_BASKET_ERROR,
  payload: {
    estr,
  },
});

export const setBasketPending: SetBasketPendingType = () => ({
  type: GridsActionTypes.SET_BASKET_PENDING,
});

export const onCheckBox: OnCheckBoxType = (matchIndex, checkType, selectionIndex) => ({
  type: GridsActionTypes.CHECK,
  payload: {
    matchIndex,
    checkType,
    selectionIndex,
  },
});

export const clearChecks: ClearChecksType = (index) => ({
  type: GridsActionTypes.CLEAR_CHECKS,
  payload: {
    index,
  },
});

export const switchType: SwitchTypeType = (gridType) => ({
  type: GridsActionTypes.SWITCH_TYPE,
  payload: { gridType },
});

export const setStake: SetStakeType = (betAmount) => ({
  type: GridsActionTypes.SET_STAKE,
  payload: { betAmount },
});

export const togglePrivate: TogglePrivateGridType = () => ({
  type: GridsActionTypes.TOGGLE_PRIVATE_MODE,
});

export const toggleSystem: ToggleSystemGridType = () => ({
  type: GridsActionTypes.TOGGLE_SYSTEM_MODE,
});

export const removeSystemGrid: RemoveSystemGridType = (index) => ({
  type: GridsActionTypes.REMOVE_SYSTEM_GRID,
  payload: {
    index,
  },
});

export const copySystemGrid: CopySystemGridType = (index: number) => ({
  type: GridsActionTypes.COPY_SYSTEM_GRID,
  payload: {
    index,
  },
});

export const copyGrid: CopyGridType = (grid) => ({
  type: GridsActionTypes.COPY_GRID,
  payload: {
    grid,
  },
});

export const submitBasket: SubmitBasketType = () => (dispatch, getState) => {
  const { grids: gridsState, user } = getState();
  const { selections, poolId, isPrivate, gridType, betAmount, gridReference, isSystem } = gridsState.basket;

  let selection = isSystem ? selections : selections[0];
  let isValid = true;

  if (gridReference) {
    // if we have gridReference, it's funding, we don't use basket data but public or history info
    const grid = getGrid(getState(), gridReference);
    if (grid) {
      selection = grid.isSystem ? grid.systemStrGridSelections : grid.strGridSelection;
    } else {
      const estr = __('You cannot bet on this grid anymore');
      dispatch(setBasketError(estr));
      isValid = false;
    }
  } else if (!Selection.hasEnoughChecksForAllSelections(selections)) {
    const estr = __('You must tick at least one box per line.');
    dispatch(setBasketError(estr));
    isValid = false;
  }

  if (isValid) {
    dispatch(setBasketPending());
    dispatch({ type: GridsActionTypes.SUBMIT_BASKET });
    const submitData = {
      bet_amount: betAmount === '' ? 1 : parseInt(betAmount, 10),
      grid_reference: gridReference,
      grid_type: gridType,
      is_private: !!isPrivate,
      pool_id: poolId,
      str_grid_selection: selection,
    };
    const { sessionId, cc, jwt: jwtInfos } = user ?? {};
    const { token: jwt } = jwtInfos ?? {};

    const form = `grid=${JSON.stringify(submitData)}`;
    const url = basketGridUrl[getLang()];
    const method = 'POST';
    const credentials = 'same-origin';
    const options = {
      url,
      method,
      credentials,
      body: form,
      forwardWamUserIp: true,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        ...getCustomHeaders({ jwt, sessionId, cc, tracker: Client.tracker }),
      },
    };

    const hooks = {
      success: (response) => {
        if (response) {
          const { ecode, url } = response;

          // redirect because user limits are not registered
          if (url !== undefined) {
            dispatch(externalLinkOpen(getRedirectUrl(RedirectServices.ENCODED_URL, { url }), { auth: true }));
            return;
          }

          switch (ecode) {
            case 0: {
              /**
               * HANDLE SUCCESS
               */
              const { gid } = response;
              dispatch(setBasketValidated({ gridReference: gid }));
              break;
            }
            case GridsErrorCodes.ACCESS_DENIED: {
              dispatch(setBasketError(Basket.getErrorCodeMessage(response)));
              break;
            }
            case GridsErrorCodes.NOT_ENOUGH_MONEY:
            case GridsErrorCodes.INVALID_BET_AMOUNT:
            case GridsErrorCodes.MULTIPLE_CHECKS_REQUIRED:
            case GridsErrorCodes.MODERATOR_REACHED:
            case GridsErrorCodes.DAILY_MODERATOR_REACHED:
            case GridsErrorCodes.WEEKLY_MODERATOR_REACHED:
            case GridsErrorCodes.MONTHLY_MODERATOR_REACHED:
            case GridsErrorCodes.GRID_NOT_AVAILABLE:
            case GridsErrorCodes.GRID_INCOMPLETE:
            default: {
              dispatch(setBasketError(Basket.getErrorCodeMessage(response)));
              break;
            }
          }
        }
      },
      failure: (err, extra) => {
        dispatch(
          sendReport({
            message: 'validate_grid failure',
            error: err.toString(),
            extra,
          }),
        );

        Sentry.captureMessage('User sign-out from validate_grid failure');
        dispatch(setBasketError(__('Service unavailable')));
      },
    };

    dispatch(
      makeApiCall({
        options,
        hooks,
      }),
    );
  }
};
